import React, { useState, useEffect } from 'react';
import { Form, Table, Button, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import { adminUserApi, adminWarehouseApi } from 'api';
import WarehousePermissionModal from './WarehousePermissionModal';
import { PERMISSION_LABELS } from 'constants/admin/user';

const buildPermissionMap = (warehousePermissions, fieldDefs) => {
  const map = {};
  (warehousePermissions || []).forEach(perm => {
    const entry = { enabled: true, is_manager: perm.is_manager };
    fieldDefs.forEach(f => {
      entry[f.key] = perm[f.key] ?? 0;
    });
    map[perm.warehouse.id] = entry;
  });
  return map;
};

const WarehousePermissionEdit = ({
  user,
  permFieldDefs = [],
  onSuccess,
  onCancel,
}) => {
  const [warehouses, setWarehouses] = useState([]);
  const [permMap, setPermMap] = useState({});

  useEffect(() => {
    if (permFieldDefs.length === 0) return;
    setPermMap(prev => {
      const fresh = buildPermissionMap(
        user?.warehouse_permissions,
        permFieldDefs,
      );
      if (Object.keys(prev).length === 0) return fresh;
      const merged = { ...prev };
      Object.entries(fresh).forEach(([whId, freshEntry]) => {
        const existing = merged[whId];
        if (!existing) {
          merged[whId] = freshEntry;
          return;
        }
        permFieldDefs.forEach(f => {
          if (!(f.key in existing)) {
            existing[f.key] = freshEntry[f.key] ?? 0;
          }
        });
      });
      return merged;
    });
  }, [permFieldDefs, user?.warehouse_permissions]);

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [error, setError] = useState(null);
  const [modalWarehouse, setModalWarehouse] = useState(null);

  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const res = await adminWarehouseApi.getList({ page_size: 9999 });
        setWarehouses(res?.results || res || []);
      } catch {
        setFetchError('Failed to load warehouse list');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchWarehouses();
  }, []);

  const handleToggle = (warehouseId, checked) => {
    setPermMap(prev => {
      const defaults = { is_manager: false };
      permFieldDefs.forEach(f => {
        defaults[f.key] = 0;
      });
      return {
        ...prev,
        [warehouseId]: {
          ...defaults,
          ...prev[warehouseId],
          enabled: checked,
        },
      };
    });
  };

  const handleModalSave = (warehouseId, updatedPerm) => {
    setPermMap(prev => ({
      ...prev,
      [warehouseId]: { ...prev[warehouseId], ...updatedPerm, enabled: true },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = Object.entries(permMap)
        .filter(([, perm]) => perm.enabled)
        .map(([warehouseId, perm]) => {
          const entry = {
            warehouse_id: Number(warehouseId),
            is_manager: perm.is_manager,
          };
          permFieldDefs.forEach(f => {
            entry[f.key] = perm[f.key] ?? 0;
          });
          return entry;
        });

      await adminUserApi.updatePermissions(user.id, payload);
      toast.success('Permissions updated successfully');
      await onSuccess();
    } catch (err) {
      const msg =
        err?.response?.data?.permissions?.[0] || 'Failed to update permissions';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <p className="text-muted mb-0">Loading warehouses...</p>;
  }

  if (fetchError) {
    return (
      <>
        <Alert variant="danger">{fetchError}</Alert>
        <div className="d-flex justify-content-end">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </>
    );
  }

  if (!warehouses.length) {
    return (
      <>
        <p className="text-muted">No warehouses available.</p>
        <div className="d-flex justify-content-end">
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </>
    );
  }

  const modalPerm = modalWarehouse ? permMap[modalWarehouse.id] || {} : {};

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Table responsive bordered size="sm" className="mb-3">
        <thead>
          <tr>
            <th className="text-center" style={{ width: '60px' }}>
              Assign
            </th>
            <th>Warehouse</th>
            <th className="text-center">Manager</th>
            {permFieldDefs.map(f => (
              <th key={f.key} className="text-center">
                {f.label}
              </th>
            ))}
            <th className="text-center" style={{ width: '60px' }} />
          </tr>
        </thead>
        <tbody>
          {warehouses.map(wh => {
            const perm = permMap[wh.id];
            const enabled = !!perm?.enabled;

            return (
              <tr key={wh.id} className={!enabled ? 'table-light' : undefined}>
                <td className="text-center align-middle">
                  <Form.Check
                    type="checkbox"
                    checked={enabled}
                    onChange={e => handleToggle(wh.id, e.target.checked)}
                  />
                </td>
                <td className="align-middle">{wh.name}</td>
                <td className="text-center align-middle">
                  {enabled ? (perm?.is_manager ? 'Yes' : 'No') : '-'}
                </td>
                {permFieldDefs.map(f => (
                  <td key={f.key} className="text-center align-middle">
                    {enabled
                      ? PERMISSION_LABELS[perm?.[f.key]] ??
                        perm?.[f.key] ??
                        'None'
                      : '-'}
                  </td>
                ))}
                <td className="text-center align-middle">
                  {enabled && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setModalWarehouse(wh)}
                    >
                      <FontAwesomeIcon icon="edit" />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="success"
          size="sm"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Permissions'}
        </Button>
      </div>

      {modalWarehouse && (
        <WarehousePermissionModal
          show
          onHide={() => setModalWarehouse(null)}
          warehouse={modalWarehouse}
          permission={modalPerm}
          permFieldDefs={permFieldDefs}
          editing
          onSave={handleModalSave}
        />
      )}
    </>
  );
};

export default WarehousePermissionEdit;
